//@ts-check
if (!customElements.get('c-container-video')) {
  const MOBILE_QUERY = '(max-width: 1023px)'
  const VISIBILITY_THRESHOLD = 0.25

  /**
   * Écarte un libellé non résolu par Liquid (clé de traduction absente) plutôt que
   * d'écrire « Translation missing » dans un aria-label.
   * @param {string | undefined} value
   */
  const cleanLabel = (value) =>
    value && !value.includes('Translation missing') ? value : null

  class ContainerVideo extends HTMLElement {
    constructor() {
      super()
      this.boundTogglePlay = () => this.togglePlay()
      this.boundToggleMute = () => this.toggleMute()
      this.boundPrimeVideo = () => this.primeActiveVideo()
      this.boundPauseFromHitArea = () => this.toggleFromHitArea()
      this.boundBreakpointChange = () => this.handleBreakpointChange()
      this.boundVideoStateChange = (event) => this.handleVideoStateChange(event)

      this.hasPlayed = false
      /** Pause demandée explicitement : la remise en vue ne doit pas relancer la lecture. */
      this.userPaused = false
      /** `play()` refusé par le navigateur : les contrôles doivent repasser en lecture. */
      this.playBlocked = false
      this.isInView = false
      /** @type {HTMLVideoElement | null} */
      this.activeVideo = null
      /** @type {MediaQueryList | null} */
      this.mediaQuery = null
      /** @type {IntersectionObserver | null} */
      this.visibilityObserver = null
    }

    connectedCallback() {
      const playBtn = this.querySelector('[data-play-btn]')
      const muteBtn = this.querySelector('[data-mute-btn]')
      const pauseHitArea = this.querySelector('[data-video-pause-hit]')

      if (playBtn) {
        playBtn.addEventListener('click', this.boundTogglePlay)
        playBtn.addEventListener('pointerdown', this.boundPrimeVideo)
        playBtn.addEventListener('touchstart', this.boundPrimeVideo, {
          passive: true,
        })
      }

      if (muteBtn) {
        muteBtn.addEventListener('click', this.boundToggleMute)
      }

      if (pauseHitArea) {
        pauseHitArea.addEventListener('click', this.boundPauseFromHitArea)
      }

      // `getActiveVideo()` dépend du breakpoint : sans cet écouteur, redimensionner
      // la fenêtre laissait les contrôles branchés sur la vidéo désormais masquée.
      this.mediaQuery = window.matchMedia(MOBILE_QUERY)
      this.mediaQuery.addEventListener('change', this.boundBreakpointChange)

      this.querySelectorAll('video').forEach((video) => {
        video.addEventListener('play', this.boundVideoStateChange)
        video.addEventListener('pause', this.boundVideoStateChange)
        video.style.cursor = 'pointer'
      })

      this.activeVideo = this.getActiveVideo()
      this.deactivateInactiveVideos()

      // La lecture suit la visibilité réelle. Une vidéo lancée hors viewport est
      // suspendue par le navigateur puis relancée en boucle, ce qui réécrivait les
      // contrôles toutes les quelques secondes indéfiniment.
      this.visibilityObserver = new IntersectionObserver(
        (entries) => this.handleVisibilityChange(entries),
        { threshold: VISIBILITY_THRESHOLD }
      )
      this.visibilityObserver.observe(this)

      this.syncPlayControls()
    }

    disconnectedCallback() {
      const playBtn = this.querySelector('[data-play-btn]')
      const muteBtn = this.querySelector('[data-mute-btn]')
      const pauseHitArea = this.querySelector('[data-video-pause-hit]')

      if (playBtn) {
        playBtn.removeEventListener('click', this.boundTogglePlay)
        playBtn.removeEventListener('pointerdown', this.boundPrimeVideo)
        playBtn.removeEventListener('touchstart', this.boundPrimeVideo)
      }

      if (muteBtn) {
        muteBtn.removeEventListener('click', this.boundToggleMute)
      }

      if (pauseHitArea) {
        pauseHitArea.removeEventListener('click', this.boundPauseFromHitArea)
      }

      this.mediaQuery?.removeEventListener('change', this.boundBreakpointChange)
      this.mediaQuery = null

      this.visibilityObserver?.disconnect()
      this.visibilityObserver = null

      this.querySelectorAll('video').forEach((video) => {
        video.removeEventListener('play', this.boundVideoStateChange)
        video.removeEventListener('pause', this.boundVideoStateChange)
      })
    }

    getActiveVideo() {
      const isMobile = this.mediaQuery
        ? this.mediaQuery.matches
        : window.matchMedia(MOBILE_QUERY).matches
      const mobileEl = this.querySelector('.video-mobile video')
      const desktopEl = this.querySelector('.video-desktop video')
      return /** @type {HTMLVideoElement | null} */ (
        isMobile ? mobileEl || desktopEl : desktopEl || mobileEl
      )
    }

    /**
     * Les deux variantes partagent la même source : seule celle du breakpoint courant
     * doit être chargée, sinon la vidéo est téléchargée deux fois.
     */
    deactivateInactiveVideos() {
      this.querySelectorAll('video').forEach((video) => {
        if (video === this.activeVideo) return
        video.preload = 'none'
        this.setAttr(video, 'preload', 'none')
        video.removeAttribute('autoplay')
        if (!video.paused) video.pause()
      })
    }

    /**
     * @param {Element} element
     * @param {string} name
     * @param {string} value
     */
    setAttr(element, name, value) {
      if (element.getAttribute(name) === value) return
      element.setAttribute(name, value)
    }

    /**
     * `defaultMuted`, `playsInline` et `preload` reflètent un attribut : les affecter
     * réécrit le DOM même à valeur identique. On ne les touche donc qu'en cas d'écart.
     * @param {HTMLVideoElement} video
     */
    prepareVideo(video) {
      if (!video.muted) video.muted = true
      if (!video.defaultMuted) video.defaultMuted = true
      if (!video.playsInline) video.playsInline = true
      if (video.preload !== 'auto') video.preload = 'auto'
      this.setAttr(video, 'webkit-playsinline', '')
      if (video.networkState === video.NETWORK_EMPTY) video.load()
    }

    primeActiveVideo() {
      if (!this.activeVideo) return
      this.prepareVideo(this.activeVideo)
    }

    /** @param {IntersectionObserverEntry[]} entries */
    handleVisibilityChange(entries) {
      const entry = entries[entries.length - 1]
      this.isInView = entry.isIntersecting

      if (this.isInView) {
        if (!this.userPaused) this.playActiveVideo()
      } else {
        this.pauseActiveVideo()
      }
    }

    playActiveVideo() {
      const video = this.activeVideo
      if (!video || !video.paused) return

      this.prepareVideo(video)
      const played = video.play()
      if (played && typeof played.catch === 'function') {
        played.catch((error) => {
          this.playBlocked = true
          console.warn('[c-container-video] Video play prevented.', error)
          this.syncPlayControls()
        })
      }
    }

    pauseActiveVideo() {
      const video = this.activeVideo
      if (!video || video.paused) return
      video.pause()
    }

    /**
     * Les écouteurs sont posés sur toutes les vidéos du composant, mais l'UI ne
     * reflète que la vidéo active : ignorer les événements de la variante masquée.
     * @param {Event} event
     */
    handleVideoStateChange(event) {
      if (event.target !== this.activeVideo) return
      if (event.type === 'play') {
        this.hasPlayed = true
        this.playBlocked = false
      }
      this.syncPlayControls()
    }

    /**
     * Intention de lecture, et non état instantané de `video.paused` : pendant le
     * remplissage du buffer le navigateur émet des `pause`/`play` transitoires toutes
     * les deux secondes, qui feraient clignoter les contrôles sans rien changer pour
     * l'utilisateur.
     */
    get isPlayIntended() {
      return this.isInView && !this.userPaused && !this.playBlocked
    }

    handleBreakpointChange() {
      const next = this.getActiveVideo()
      if (next === this.activeVideo) return

      const wasPlaying = this.activeVideo ? !this.activeVideo.paused : false
      this.activeVideo = next
      this.deactivateInactiveVideos()

      if (next && wasPlaying && this.isInView && !this.userPaused) {
        this.playActiveVideo()
      }

      this.syncPlayControls()
    }

    /**
     * Écrit uniquement en cas de changement réel : un `setAttribute` redondant compte
     * comme une mutation du DOM et fait clignoter le nœud dans l'inspecteur.
     * @param {Element | null} element
     * @param {boolean} hidden
     */
    setHidden(element, hidden) {
      if (!element || element.hasAttribute('hidden') === hidden) return
      if (hidden) element.setAttribute('hidden', '')
      else element.removeAttribute('hidden')
    }

    /**
     * @param {Element | null} element
     * @param {string | null} label
     */
    setLabel(element, label) {
      if (!element || !label) return
      if (element.getAttribute('aria-label') === label) return
      element.setAttribute('aria-label', label)
    }

    syncPlayControls() {
      const video = this.activeVideo
      const playBtn = this.querySelector('[data-play-btn]')
      const pauseHitArea = this.querySelector('[data-video-pause-hit]')
      if (!video || !playBtn) return

      const iconPause = playBtn.querySelector('.video-btn__icon--pause')
      const iconPlay = playBtn.querySelector('.video-btn__icon--play')
      const paused = !this.isPlayIntended

      this.setHidden(iconPause, paused)
      this.setHidden(iconPlay, !paused)
      this.setHidden(pauseHitArea, paused && !this.hasPlayed)
      this.setLabel(
        playBtn,
        cleanLabel(paused ? this.dataset.labelPlay : this.dataset.labelPause)
      )
    }

    // Le toggle suit ce que l'utilisateur voit — l'intention — et non `video.paused`,
    // qui peut être momentanément vrai pendant le remplissage du buffer.
    togglePlay() {
      const video = this.activeVideo
      if (!video) return

      if (this.isPlayIntended) {
        this.userPaused = true
        video.pause()
      } else {
        this.userPaused = false
        this.playBlocked = false
        this.playActiveVideo()
      }
      this.syncPlayControls()
    }

    toggleFromHitArea() {
      this.togglePlay()
    }

    toggleMute() {
      const muteBtn = this.querySelector('[data-mute-btn]')
      const video = this.activeVideo
      if (!muteBtn || !video) return

      const nextMuted = !video.muted
      this.querySelectorAll('video').forEach((v) => {
        v.muted = nextMuted
      })

      const iconUnmuted = muteBtn.querySelector('.video-btn__icon--unmuted')
      const iconMuted = muteBtn.querySelector('.video-btn__icon--muted')

      this.setHidden(iconUnmuted, nextMuted)
      this.setHidden(iconMuted, !nextMuted)
      this.setLabel(
        muteBtn,
        cleanLabel(nextMuted ? this.dataset.labelUnmute : this.dataset.labelMute)
      )
    }
  }
  customElements.define('c-container-video', ContainerVideo)
}
